"use client";

import Gleap from "gleap";
import { useEffect } from "react";
import { create } from "zustand";

const HIDDEN_STORAGE_KEY = "hide-gleap-button";
const CHOSEN_STORAGE_KEY = "hide-gleap-button-chosen";

type GleapButtonStore = {
  /** User preference (persisted per browser): hide the button everywhere. */
  hiddenByUser: boolean;
  setHiddenByUser: (hidden: boolean) => void;
  /** Whether the user set the preference themselves, instead of a screen doing it for them. */
  chosenByUser: boolean;
  /** Whether the persisted preference has been read yet. */
  hydrated: boolean;
  hydrate: () => void;
  /** Screens the button gets in the way of, unless the user decided otherwise. */
  autoHideRequests: number;
  addAutoHideRequest: () => void;
  removeAutoHideRequest: () => void;
  /** Screens where the button is hidden whatever the preference is. */
  hideRequests: number;
  addHideRequest: () => void;
  removeHideRequest: () => void;
};

// localStorage isn't available while rendering on the server, so the preference
// is only read once we are on the client. When storage is blocked (site data
// disabled), touching it throws: fall back to the defaults.
const readStoredPreference = () => {
  try {
    return {
      hiddenByUser: localStorage.getItem(HIDDEN_STORAGE_KEY) === "true",
      chosenByUser: localStorage.getItem(CHOSEN_STORAGE_KEY) === "true",
      hydrated: true,
    };
  } catch {
    return { hiddenByUser: false, chosenByUser: false, hydrated: true };
  }
};

const useGleapButtonStore = create<GleapButtonStore>((set) => ({
  hiddenByUser: false,
  setHiddenByUser: (hidden) => {
    // Blocked storage only costs persistence: the choice still applies until
    // the page is reloaded
    try {
      localStorage.setItem(HIDDEN_STORAGE_KEY, String(hidden));
      localStorage.setItem(CHOSEN_STORAGE_KEY, "true");
    } catch {}
    set({ hiddenByUser: hidden, chosenByUser: true, hydrated: true });
  },
  chosenByUser: false,
  hydrated: false,
  hydrate: () =>
    set((state) => (state.hydrated ? state : readStoredPreference())),
  autoHideRequests: 0,
  addAutoHideRequest: () =>
    set((state) => ({ autoHideRequests: state.autoHideRequests + 1 })),
  removeAutoHideRequest: () =>
    set((state) => ({
      autoHideRequests: Math.max(0, state.autoHideRequests - 1),
    })),
  hideRequests: 0,
  addHideRequest: () =>
    set((state) => ({ hideRequests: state.hideRequests + 1 })),
  removeHideRequest: () =>
    set((state) => ({ hideRequests: Math.max(0, state.hideRequests - 1) })),
}));

/**
 * What the "hide support button" switch shows: the user preference, or a screen
 * asking for the button to be out of the way as long as the user has not
 * decided for themselves.
 */
const isHiddenByPreference = (state: GleapButtonStore) =>
  state.hiddenByUser || (!state.chosenByUser && state.autoHideRequests > 0);

/**
 * Imperative counterpart of useSyncGleapButtonVisibility(), for the one case
 * that hook can't cover: Gleap.destroy() clears Gleap's own button state, so a
 * re-initialize (locale change) needs the visibility applied again - and the
 * hook's effect won't re-run, since none of the state it watches changed.
 * Not redundant: keep the call right after Gleap.initialize().
 */
export function applyGleapButtonVisibility() {
  const state = useGleapButtonStore.getState();

  Gleap.showFeedbackButton(!isHiddenByPreference(state) && !state.hideRequests);
}

/** The "hide support button" switch, for the account menus. */
export function useGleapButtonPreference() {
  const hidden = useGleapButtonStore(isHiddenByPreference);
  const setHidden = useGleapButtonStore((state) => state.setHiddenByUser);

  return { hidden, setHidden };
}

/**
 * Hide the Gleap button while `active` is true and the calling component is
 * mounted, for screens it gets in the way of (the advanced editor). Shown in
 * the menu as if the user had hidden it, so they can turn it back on - and once
 * they do, their choice wins here too.
 */
export function useAutoHideGleapButton(active: boolean) {
  const addAutoHideRequest = useGleapButtonStore(
    (state) => state.addAutoHideRequest,
  );
  const removeAutoHideRequest = useGleapButtonStore(
    (state) => state.removeAutoHideRequest,
  );

  useEffect(() => {
    if (!active) {
      return;
    }

    addAutoHideRequest();

    return removeAutoHideRequest;
  }, [active, addAutoHideRequest, removeAutoHideRequest]);
}

/**
 * Hide the Gleap button while `active` is true and the calling component is
 * mounted, whatever the user preference is (impersonation).
 */
export function useHideGleapButton(active: boolean) {
  const addHideRequest = useGleapButtonStore((state) => state.addHideRequest);
  const removeHideRequest = useGleapButtonStore(
    (state) => state.removeHideRequest,
  );

  useEffect(() => {
    if (!active) {
      return;
    }

    addHideRequest();

    return removeHideRequest;
  }, [active, addHideRequest, removeHideRequest]);
}

/**
 * Applies the visibility whenever it changes. Call it once, next to
 * Gleap.initialize(), so scattered showFeedbackButton() calls can't fight each
 * other depending on effect order: everything else in the app declares intent
 * through the hooks above. Together with applyGleapButtonVisibility(), which
 * covers re-initialization, these are the only two showFeedbackButton() calls.
 */
export function useSyncGleapButtonVisibility() {
  const hidden = useGleapButtonStore(isHiddenByPreference);
  const hideRequests = useGleapButtonStore((state) => state.hideRequests);
  const hydrate = useGleapButtonStore((state) => state.hydrate);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  useEffect(() => {
    Gleap.showFeedbackButton(!hidden && hideRequests === 0);
  }, [hidden, hideRequests]);
}
